import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { URLModal } from 'src/components/common/URLModal';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { RadioButton } from 'src/components/radioButton/RadioButton';
import { useRouteParams } from 'src/hooks/useRouteParams';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { variantRouteConfig } from '../../routes';

interface Props {
  // Ideally this would be a fragment.
  data: GraphQLTypes.VariantGeneralSettingsPageQuery;
  isProtected: boolean;
  hasUserRoleCapabilities: boolean;
  isEnterprise: boolean;
}

export const ProtectedVariantSettingsCard = ({
  data,
  isProtected,
  hasUserRoleCapabilities,
  isEnterprise,
}: Props) => {
  const { graphId, graphVariant } = useRouteParams(variantRouteConfig);
  const history = useHistory();
  const location = useLocation();
  const graph = data?.graph;
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Protected: {isProtected ? 'ON' : 'OFF'}</CardTitle>
          <CardDescription>
            {isEnterprise ? (
              isProtected ? (
                <>
                  Only Graph Admins and Org Admins can update this variant's
                  schema or change its configuration.
                </>
              ) : (
                <>
                  Contributors, Graph Admins, and Org Admins can update this
                  variant's schema and change its configuration.{' '}
                </>
              )
            ) : (
              <>
                This is an Enterprise feature. Protecting a variant will
                restrict the user roles who can push new schema or change this
                variant's configuration to just Graph Admins and Org Admins{' '}
                <ClickableText
                  className="text-blue-light"
                  as="a"
                  href="https://www.apollographql.com/docs/studio/org/graphs/#protected-variants-enterprise-only"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  (read more)
                </ClickableText>
                .
              </>
            )}
          </CardDescription>
          <PermissionWarning
            graphId={graphId}
            graphPermissions="canManageAccess"
            childRenderer={(canManageVariants) => {
              return (
                <Tooltip
                  label={
                    !hasUserRoleCapabilities
                      ? "Your organization's plan does not allow you to configure protected variants. Please contact support to learn more about our available plans and upgrade your subscription."
                      : !canManageVariants
                        ? 'You are not allowed to change this setting. Please contact a graph administrator or reach out to support if you need help.'
                        : ''
                  }
                  disabled={hasUserRoleCapabilities && canManageVariants}
                >
                  <Button
                    variant="destructive"
                    isDisabled={!canManageVariants || !hasUserRoleCapabilities}
                    onClick={() => {
                      history.push(
                        setOverlayVisibleLocation({
                          location,
                          overlayName: Config.modals.updateProtectedVariant,
                        }),
                      );
                    }}
                  >
                    Change
                  </Button>
                </Tooltip>
              );
            }}
          />
        </CardHeader>
      </Card>

      {graph && (
        <URLModal name={Config.modals.updateProtectedVariant}>
          {({ isOpen, closeModal }) => (
            <UpdatedProtectedVariantStatusModal
              isOpen={isOpen}
              isProtected={isProtected}
              graphVariant={graphVariant || ''}
              graphId={graph.id}
              closeModal={closeModal}
              hasUserRoleCapabilities={hasUserRoleCapabilities ?? false}
            />
          )}
        </URLModal>
      )}
    </>
  );
};

function UpdatedProtectedVariantStatusModal({
  graphId,
  graphVariant,
  isOpen,
  isProtected,
  hasUserRoleCapabilities,
  closeModal,
}: {
  graphId: string;
  graphVariant: string;
  isOpen: boolean;
  isProtected: boolean;
  hasUserRoleCapabilities: boolean;
  closeModal: () => void;
}) {
  const [toggleSetting, setToggleSetting] = useState<boolean>(isProtected);
  const [updateIsProtected, { loading, error }] = useMutation<
    GraphQLTypes.UpdateIsProtected,
    GraphQLTypes.UpdateIsProtectedVariables
  >(
    gql`
      mutation UpdateIsProtected(
        $graphId: ID!
        $graphVariant: String!
        $isProtected: Boolean!
      ) {
        service(id: $graphId) {
          variant(name: $graphVariant) {
            updateIsProtected(isProtected: $isProtected) {
              id
              isProtected
            }
          }
        }
      }
    `,
    {
      onCompleted: () => closeModal(),
    },
  );

  return (
    <PermissionWarning
      graphId={graphId}
      graphPermissions={['canManageVariants']}
      childRenderer={(canManageVariants) => (
        <Modal isOpen={isOpen} size="lg" onClose={closeModal}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                Change {graphVariant}'s "Protected" Setting
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              <RadioButton
                title={`On${isProtected ? ' (current)' : ''}`}
                description="Only Graph Admins and Org Admins can push a new schema to this variant or change its schema."
                disabled={
                  loading || !canManageVariants || !hasUserRoleCapabilities
                }
                selected={toggleSetting}
                onClick={() => {
                  setToggleSetting(true);
                }}
              />
              <RadioButton
                title={`Off${isProtected ? '' : ' (current)'}`}
                description="Contributors, Graph Admins, and Org Admins can push new schema versions to this variant and change its configuration."
                disabled={
                  loading || !canManageVariants || !hasUserRoleCapabilities
                }
                selected={!toggleSetting}
                onClick={() => {
                  setToggleSetting(false);
                }}
              />
              {error && <ErrorMessage />}
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <Button variant="secondary" onClick={() => closeModal()}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  isDisabled={loading || isProtected === toggleSetting}
                  isLoading={loading}
                  loadingText="Save"
                  onClick={() =>
                    isProtected !== toggleSetting
                      ? updateIsProtected({
                          variables: {
                            graphId,
                            graphVariant,
                            isProtected: toggleSetting,
                          },
                        })
                      : closeModal()
                  }
                >
                  Save
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    />
  );
}
